import { onAuthStateChange, AuthUser, getUserProfile } from './firebase/auth'
import { useAppStore } from '@cya-routes/shared'
import type { User } from '@cya-routes/shared'

/**
 * Authentication Initializer Service
 * 
 * Bridges Firebase Authentication with Zustand App Store
 * Sets up listeners to keep auth state synchronized
 */
export class AuthInitializer {
  private unsubscribe: (() => void) | null = null

  /**
   * Initialize authentication state synchronization
   * Call this once when the app starts
   */
  public initialize(): void {
    // Set up Firebase auth state listener
    this.unsubscribe = onAuthStateChange(async (firebaseUser: AuthUser | null) => {
      const store = useAppStore.getState()
      
      if (firebaseUser) {
        try {
          // Get additional user data from Firestore
          const firestoreProfile = await getUserProfile(firebaseUser.uid)
          
          // Map Firebase user to Zustand User format, merging Firestore data
          const zustandUser = this.mapFirebaseUserToZustandUser(firebaseUser, firestoreProfile)
          const profileNeedsCompletion = this.requiresProfileCompletion(firebaseUser, firestoreProfile)

          // Update Zustand store with authenticated state
          store.setAuthState({
            currentUser: zustandUser,
            isAuthenticated: true,
            authToken: undefined, // Firebase handles tokens internally
            isLoading: false,
            profileNeedsCompletion
          })
          
          console.log('✅ User authenticated and synced to store with Firestore data:', zustandUser.email)
        } catch (error) {
          console.error('Error loading user profile from Firestore:', error)
          
          // Fall back to Firebase Auth data only
          const zustandUser = this.mapFirebaseUserToZustandUser(firebaseUser)
          const profileNeedsCompletion = this.requiresProfileCompletion(firebaseUser)

          store.setAuthState({
            currentUser: zustandUser,
            isAuthenticated: true,
            authToken: undefined,
            isLoading: false,
            profileNeedsCompletion
          })
          
          console.log('✅ User authenticated with Firebase Auth data only:', zustandUser.email)
        }
      } else {
        // User signed out - clear auth state
        store.setAuthState({
          currentUser: null,
          isAuthenticated: false,
          authToken: undefined,
          isLoading: false,
          profileNeedsCompletion: false
        })
        
        console.log('🔒 User signed out - auth state cleared')
      }
    })

    console.log('🔄 Firebase auth state listener initialized')
  }

  /**
   * Clean up listeners when app unmounts
   */
  public cleanup(): void {
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
      console.log('🧹 Firebase auth state listener cleaned up')
    }
  }

  /**
   * Maps Firebase AuthUser to Zustand User format
   * Handles differences in data structure between Firebase and app store
   * Merges with Firestore profile data if available
   */
  private mapFirebaseUserToZustandUser(firebaseUser: AuthUser, firestoreProfile?: any): User {
    // Use Firestore data if available, otherwise fall back to Firebase Auth data
    const authDisplayName = firebaseUser.displayName || ''

    // Use Firestore displayName if available, otherwise Firebase Auth displayName
    const rawDisplayName = firestoreProfile?.displayName || authDisplayName || `${firebaseUser.email?.split('@')[0] || 'User'}`
    const normalizedDisplayName = this.normalizeDisplayName(rawDisplayName)

    // Split displayName for backwards compatibility with existing User type
    const splitName = normalizedDisplayName.split(' ')
    const firstName = splitName[0] || 'User'
    const lastName = splitName.slice(1).join(' ') || ''
    
    // Generate username from email for now (could be enhanced later)
    const username = this.generateUsername(firebaseUser.email, normalizedDisplayName)
    
    // Build location from city/country if available in Firestore
    const location = (firestoreProfile?.city || firestoreProfile?.country) ? {
      region: firestoreProfile.city || '',
      country: firestoreProfile.country || '',
      state: firestoreProfile.city || ''
    } : firestoreProfile?.location

    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      firstName,
      lastName,
      displayName: normalizedDisplayName,
      username,
      profilePhoto: firebaseUser.photoURL || undefined,
      role: firestoreProfile?.role || firebaseUser.role || 'user',
      location,
      createdAt: new Date(), // This should come from Firestore profile
      lastActive: new Date(),
      emailVerified: true, // Firebase handles email verification
      
      // Default stats for new users
      stats: {
        totalRoutes: 0,
        publishedRoutes: 0,
        premiumRoutes: 0,
        totalDistance: 0,
        clubMemberships: 1, // Auto-joined to "Bike Routes" default club
        followedClubs: 0
      },
      
      // Default subscription
      subscription: {
        tier: 'free',
        features: []
      }
    }
  }

  /**
   * Generate a unique username from email or display name
   */
  private generateUsername(email: string | null, displayName: string | null): string {
    if (displayName) {
      // Convert display name to username format
      return displayName
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .substring(0, 20)
    }
    
    if (email) {
      // Use email prefix as username
      return email
        .split('@')[0]
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 20)
    }
    
    // Fallback to random username
    return `user-${Math.random().toString(36).substring(2, 8)}`
  }

  /**
   * Ensure display names contain at least two words for better personalization
   */
  private normalizeDisplayName(value: string | null): string {
    const trimmed = (value || '').trim().replace(/\s+/g, ' ')
    if (!trimmed) {
      return 'New Rider'
    }

    const parts = trimmed.split(' ')
    if (parts.length >= 2) {
      return trimmed
    }

    // Encourage two-word style by appending a descriptive suffix
    return `${trimmed} Routes`
  }

  /**
   * Determine whether we should prompt the rider to complete their profile
   */
  private requiresProfileCompletion(firebaseUser: AuthUser, firestoreProfile?: any): boolean {
    const firstNameSource = (firestoreProfile?.firstName ?? '').toString().trim()
    const displayNameSource = (
      firestoreProfile?.displayName ??
      firebaseUser.displayName ??
      ''
    ).toString().trim()

    const hasFirstName = firstNameSource.length > 0 && firstNameSource.toLowerCase() !== 'user'
    const displayNameParts = displayNameSource.split(/\s+/).filter(Boolean)
    const hasTwoWordDisplayName = displayNameParts.length >= 2

    return !(hasFirstName && hasTwoWordDisplayName)
  }
}

// Export singleton instance
export const authInitializer = new AuthInitializer()
